import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowRightLeft, RadioTower } from 'lucide-react'
import { liveSourcesApi, playoutApi } from '../../lib/api.js'
import { liveSourceSwitchCandidates } from '../../lib/liveSourceSwitch.js'
import { useStore } from '../../store/index.js'
import type { LiveSource, ScheduleItem } from '../../types/index.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'
import './LiveSourceSwitchDialog.css'

interface LiveSourceSwitchDialogProps {
  channelId: string
  item:      Pick<ScheduleItem, 'id' | 'liveSourceId' | 'liveSource'>
  onClose:   () => void
}

const STATUS_LABEL: Record<string, string> = {
  ACTIVE:      'vu actif',
  UNSTABLE:    'instable',
  SIGNAL_LOST: 'signal perdu',
  INACTIVE:    'jamais vu',
}

/**
 * « Changer de flux » d'un direct à l'antenne : ouvert depuis le bandeau du
 * direct (sous le retour antenne) ou le clic droit sur le direct en cours dans
 * la grille. Règle et effets : `backend/src/lib/live-source-switch.ts`.
 */
export const LiveSourceSwitchDialog = memo(function LiveSourceSwitchDialog({ channelId, item, onClose }: LiveSourceSwitchDialogProps) {
  const [sources, setSources] = useState<LiveSource[] | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    liveSourcesApi.list(channelId)
      .then(list => { if (!cancelled) setSources(list) })
      .catch(cause => { if (!cancelled) setError(cause instanceof Error ? cause.message : 'Lecture des sources impossible') })
    return () => { cancelled = true }
  }, [channelId])

  const candidates = useMemo(
    () => liveSourceSwitchCandidates(sources ?? [], item, channelId),
    [channelId, item, sources],
  )
  // Un seul flux de secours : il est choisi d'office, la bascule reste un clic.
  const effectiveId = selected ?? (candidates.length === 1 ? candidates[0].id : null)
  const target = candidates.find(source => source.id === effectiveId) ?? null

  const handleSwitch = useCallback(async () => {
    if (!target || pending) return
    setPending(true)
    setError('')
    try {
      await playoutApi.switchLiveSource(channelId, item.id, target.id)
      useStore.getState().pushToast(`Bascule sur « ${target.name} » : interlude le temps que le flux s’ouvre.`, 'success', 6000)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Bascule impossible')
    } finally {
      setPending(false)
    }
  }, [channelId, item.id, onClose, pending, target])

  return (
    <ModalShell title="Changer de flux du direct" size="narrow" onClose={onClose} closeDisabled={pending} className="live-source-switch">
      <div className="tp-modal__body live-source-switch__body">
        <p className="live-source-switch__current">
          <RadioTower size={14} aria-hidden="true" />
          <span>À l’antenne : <strong>{item.liveSource?.name ?? 'flux inconnu'}</strong></span>
        </p>

        {!sources && !error && <div className="tp-empty-state"><Spinner /> Lecture des sources…</div>}
        {sources && candidates.length === 0 && (
          <div className="tp-empty-state">
            Aucun autre flux Internet actif. Ajoutez le flux de secours dans Réglages › Sources live.
          </div>
        )}

        {candidates.length > 0 && (
          <div className="live-source-switch__list" role="radiogroup" aria-label="Flux de remplacement">
            {candidates.map(source => (
              <button
                key={source.id}
                type="button"
                role="radio"
                aria-checked={effectiveId === source.id}
                className="live-source-switch__option"
                data-selected={effectiveId === source.id || undefined}
                disabled={pending}
                onClick={() => setSelected(source.id)}
              >
                <span className="live-source-switch__name">{source.name}</span>
                <span className="live-source-switch__meta">
                  {source.sourceType === 'INTERNET' ? 'Internet' : source.sourceType}
                  {' · '}
                  {STATUS_LABEL[source.status] ?? source.status}
                </span>
              </button>
            ))}
          </div>
        )}

        <p className="live-source-switch__note">
          La boucle d’interlude passe le temps que le flux s’ouvre (2 à 5 s), puis le direct
          revient sur ce flux. Tout le direct bascule, reprises après décrochage comprises.
        </p>

        {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}
      </div>
      <footer className="tp-modal__footer">
        <button type="button" className="tp-button" onClick={onClose} disabled={pending}>Annuler</button>
        <button
          type="button"
          className="tp-button tp-button--critical"
          disabled={!target || pending}
          onClick={() => { void handleSwitch() }}
        >
          {pending ? <Spinner /> : <ArrowRightLeft size={15} aria-hidden="true" />}
          {target ? `Basculer sur « ${target.name} »` : 'Basculer'}
        </button>
      </footer>
    </ModalShell>
  )
})
